/**
 * one-stop/blocks-events — the `events` block: the prototype's "This week on
 * campus".
 *
 * The row, lifted: a 32 px date tile (brand month over a chrome-ink day), the
 * title, the "when · where" line, and the registration state in its own tone —
 * green open, amber waitlist, red full — at `radius-sm` with 10/12 padding,
 * grid on desktop, one bordered list at 390 px with a 38 px tile. "All events"
 * sits at the right of the section head.
 *
 * ONE DELIBERATE DEVIATION FROM THE PROTOTYPE, and it is the honest one: in the
 * prototype the whole row is a button that opens an event detail SCREEN. This
 * renderer has no detail screen, and a row that looks clickable and does nothing
 * is a dead control. So the row carries an explicit Register action on its right
 * instead — same geometry, same type steps, same tones, real affordance.
 *
 * WHERE THE DETAILS FORM GOES, AND WHY IT IS NOT IN THE TITLE COLUMN. The header
 * is a three-column row: date tile, title column, action column. The form is a
 * SIBLING of that header, not a child of the title column — in the title column
 * it inherited whatever was left after the tile and two buttons (about 150 px on
 * the live tenant), the two-column name row halved that again, and the result was
 * 75 px inputs with labels wrapping mid-word. `lv-row-stack` makes the row a
 * column so the form owns the full card width; the header row is untouched.
 *
 * THE CTA: `registerForEvent(eventId, visitor, waitlist)` → the reference, shown
 * in place. The item already carries `eventId` and `registration.state`; nothing
 * was added for the button.
 *
 * TWO IDENTIFIERS COME BACK AND THEY ARE NOT INTERCHANGEABLE. `orderRef` is the
 * DISPLAY reference and it is what goes on screen. `data.registration.id` is the
 * HANDLE the read and cancel routes take, and it is held in state, unrendered,
 * for whatever this row is later allowed to do to the registration. A screen
 * that stores the display reference and later tries to cancel with it fails, and
 * it fails for the first time against a real tenant.
 *
 * AND THERE IS NO "REGISTER AGAIN". `register → cancel → register` on the same
 * email currently 500s on the live tenant, so the UI is driven from the state it
 * was returned: once this row has a reference, the affordance is gone rather
 * than offering a path that breaks.
 */
import { useCallback, useState } from "react";
import type { JSX } from "react";
import type { PortalEventItem, PortalEventRegistration } from "@v0y4ge/api-contracts";
import { Button } from "@v0y4ge/jrni-ui";
import { isBrowserNavigable, useCta, useOneStopActions } from "./actions";
import type { EventRegistrationRef } from "./actions";
import { configBool, configText, dateTileParts, formatMoney, formatWeekdayTime, hrefOf } from "./config";
import {
  Actions,
  CopyAction,
  CtaButton,
  CtaFeedback,
  DateTile,
  Empty,
  REGISTRATION_TONE,
  Section,
  SelectableValue,
  VisitorFields,
  fragmentOf,
  reportUnknown,
  useVisitor,
} from "./primitives";
import { plural, uiStrings } from "./strings";
import type { StringKey, Translate } from "./strings";
import type { BlockRenderProps } from "./types";

export function EventsBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "events");
  const title = configText(block.config, "title", ctx.locale) ?? t("events.title");
  const subtitle = configText(block.config, "subtitle", ctx.locale) ?? t("events.subtitle");
  const ctaLabel = configText(block.config, "ctaLabel", ctx.locale) ?? t("events.cta");
  const showCounts = configBool(block.config, "showRegistrationCounts", true);
  const showWaitlist = configBool(block.config, "showWaitlistState", true);
  const showAllLink = configBool(block.config, "showAllEventsLink", true);
  const allLabel = configText(block.config, "allEventsLabel", ctx.locale) ?? t("events.all");
  const allHref = hrefOf(block.config["allEventsTarget"], ctx.locale);

  const note =
    showAllLink && allHref ? (
      <a href={allHref} style={{ fontWeight: "var(--jrni-font-weight-semibold)" }}>
        {allLabel}
      </a>
    ) : null;

  /* GATED AND EMPTY ⇒ NOTHING AT ALL (2026-09-19, product owner). With a gate
     on the page this list's result is the NARROWED set, so an empty one does not
     mean the calendar is quiet this week — it means nothing here clears this student's holds. The
     generic empty state says the wrong thing (it reads as bad luck), and the
     heading alone would leave a titled void. The identity panel's line is the
     one explanation. Ungated behaviour is untouched. */
  if (ctx.holdGate && (!fragment || fragment.items.length === 0)) return null;

  if (!fragment || fragment.items.length === 0) {
    return (
      <Section title={title} subtitle={subtitle} note={note}>
        <Empty>{t("events.empty")}</Empty>
      </Section>
    );
  }

  const more = Math.max(0, fragment.total - fragment.items.length);

  return (
    <Section title={title} subtitle={subtitle} note={note}>
      <div className="lv-rowlist lv-tight">
        {fragment.items.map((item) => (
          <EventRow
            key={item.eventId}
            item={item}
            t={t}
            locale={ctx.locale}
            ctaLabel={ctaLabel}
            showCounts={showCounts}
            showWaitlist={showWaitlist}
          />
        ))}
      </div>
      {fragment.hasMore && more > 0 ? (
        <p className="lv-overflow">{plural(t, "events.more", more)}</p>
      ) : null}
    </Section>
  );
}

/**
 * Registration state in words. `registeredCount` / `capacity` are `null` when the
 * block's `showRegistrationCounts` is off — the field is ABSENT from the wire, so
 * nothing is re-derived and no placeholder is substituted.
 */
function registrationLabel(
  registration: PortalEventRegistration,
  t: Translate,
  showCounts: boolean,
  showWaitlist: boolean,
): string {
  const base =
    registration.state === "waitlist"
      ? showWaitlist && registration.waitlistCount !== null
        ? t("reg.waitlist.count", { n: registration.waitlistCount })
        : t("reg.waitlist")
      : t(`reg.${registration.state}` as StringKey);

  if (base === `reg.${registration.state}`) {
    // A registration state this build has not heard of: say nothing about it.
    reportUnknown("fragment", { registrationState: registration.state });
    return "";
  }
  if (!showCounts || registration.registeredCount === null) return base;
  return registration.capacity !== null
    ? t("reg.counts", { label: base, a: registration.registeredCount, b: registration.capacity })
    : t("reg.countsOnly", { label: base, a: registration.registeredCount });
}

function EventRow(props: {
  item: PortalEventItem;
  t: Translate;
  locale: string;
  ctaLabel: string;
  showCounts: boolean;
  showWaitlist: boolean;
}): JSX.Element {
  const { item, t } = props;
  const actions = useOneStopActions();
  const visitor = useVisitor();
  const [collecting, setCollecting] = useState(false);
  const [registered, setRegistered] = useState<EventRegistrationRef | null>(null);

  // `waitlist: true` is the waitlist BRANCH of the same operation, driven by the
  // state the server resolved — never by a second button.
  const waitlisting =
    item.registration.state === "waitlist" || item.registration.state === "full";

  const register = useCallback(
    () =>
      actions.registerForEvent({
        eventId: item.eventId,
        visitor: visitor.value,
        waitlist: waitlisting,
      }),
    [actions, item.eventId, visitor.value, waitlisting],
  );
  const cta = useCta(register, setRegistered, visitor.showServerErrors);

  const tile = dateTileParts(item.start, props.locale);
  const status = registrationLabel(item.registration, t, props.showCounts, props.showWaitlist);
  const tone = REGISTRATION_TONE[item.registration.state] ?? "var(--jrni-color-text-soft)";
  const whereWhen = [
    formatWeekdayTime(item.start, props.locale),
    item.locationName,
    item.price ? formatMoney(item.price, props.locale) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Only these two states have anything to submit. `closed` and `not-required`
  // are answers, not affordances.
  const canRegister =
    item.registration.state === "open" ||
    item.registration.state === "waitlist" ||
    (item.registration.state === "full" && item.registration.waitlistCount !== null);
  // Live now; the remaining reason is the preview, not an unbuilt flow.
  const reason = actions.available ? null : (actions.unavailableReason || t("cta.preview"));
  const running = cta.state.status === "running";
  const open = collecting && !registered;
  // The pre-flight: mark, focus, and send nothing when a field is wrong.
  const submit = (): void => {
    if (visitor.check()) cta.run();
  };
  /* HANDOFF: Register opens the events journey on THIS event, in a new tab.
     Same reasoning as the desk cards — the journey owns the tickets, the price
     and the consent copy, so the front door carries the event id and stops. */
  // Same rule as the desk cards: no USABLE destination, no hand-off — the
  // inline registration below is the working path, and a hand-off that resolves
  // nowhere would silently replace it with a link back to this page.
  //
  // `isBrowserNavigable` rather than truthiness, for the reason spelled out in
  // blocks-queues.tsx: `handoffUrl` applies this exact test before it will use
  // the value, and a guard that asks a weaker question hands off to no
  // destination instead of to the inline flow.
  const hand =
    item.registerUrl && isBrowserNavigable(item.registerUrl) ? actions.handoff : undefined;
  const openLabel = props.ctaLabel;

  return (
    <div className="lv-row lv-compact lv-row-stack">
      {/* `lv-row-cta` only when the head actually carries a CTA: the 176px
          Register column beside the title left an event name about 70px in a
          desk card, one word a line. With it the CTA takes its own line under
          the tile and title (theme.tsx); a registered or closed event keeps
          the plain head. */}
      <div className={canRegister && !registered ? "lv-row-head lv-row-cta" : "lv-row-head"}>
        <DateTile mon={tile.mon} day={tile.day} />
        <span className="lv-row-main" style={{ flex: 1 }}>
          <span
            style={{
              fontSize: "var(--jrni-font-size-P2)",
              fontWeight: "var(--jrni-font-weight-bold)",
              color: "var(--jrni-color-chrome-ink)",
              lineHeight: 1.3,
            }}
          >
            {item.title}
          </span>
          {whereWhen ? <span className="lv-row-detail">{whereWhen}</span> : null}
          {status ? (
            <span
              style={{
                fontSize: "var(--jrni-font-size-L1)",
                fontWeight: "var(--jrni-font-weight-semibold)",
                color: tone,
              }}
            >
              {status}
            </span>
          ) : null}
          {registered?.outcome === "already" ? (
            /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): neutral already-registered state — no reference, no copy action. CD to absorb. */
            <span className="lv-success-eyebrow" role="status">{t("event.alreadyRegistered")}</span>
          ) : registered ? (
            /* PLAIN TEXT, and deliberately so: the event registration response
               carries no manage target (JAI-1090), so there is nothing to link
               and a speculative anchor would either be dead or would hand out a
               reference as a credential. What the reference gains instead is
               being selectable on its own and having Copy beside it — it is read
               out and typed in, and that is the affordance it can actually keep
               today. */
            <span className="lv-success-eyebrow">
              {t(registered.outcome === "waitlist" ? "reg.waitlist" : "event.registered")} ·{" "}
              <SelectableValue
                sentence={t("ref", { ref: registered.reference })}
                value={registered.reference}
              />
              <CopyAction
                t={t}
                value={registered.reference}
                label={t("copy.ref", { ref: registered.reference })}
              />
            </span>
          ) : null}
          {/* While the form is open its feedback belongs BELOW the form, with the
              fields it is about — not squeezed into the title column. */}
          {!registered && !open && !hand && (reason || cta.state.status === "error") ? (
            <CtaFeedback t={t} disabledReason={reason} state={cta.state} />
          ) : null}
        </span>
        {canRegister && !registered ? (
          <span className="lv-row-side">
            <Actions>
              {hand ? (
                <span className="lv-act">
                  <Button
                    hierarchy="primary"
                    size="small"
                    onClick={() =>
                      hand({
                        kind: "event",
                        url: item.registerUrl,
                        eventId: item.eventId,
                        label: item.title,
                      })
                    }
                  >
                    {openLabel}
                  </Button>
                </span>
              ) : (
                <>
                  <CtaButton
                    label={cta.state.status === "error" ? t("cta.retry") : props.ctaLabel}
                    onClick={collecting ? submit : () => setCollecting(true)}
                    t={t}
                    hierarchy="primary"
                    disabledReason={reason}
                    running={running}
                  />
                  {collecting ? (
                    <span className="lv-act">
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() => setCollecting(false)}
                        disabled={running}
                      >
                        {t("visitor.cancel")}
                      </Button>
                    </span>
                  ) : null}
                </>
              )}
            </Actions>
          </span>
        ) : null}
      </div>
      {open ? (
        <div className="lv-row-form">
          <VisitorFields form={visitor} t={t} disabled={running} />
          <CtaFeedback t={t} disabledReason={reason} state={cta.state} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * one-stop/blocks-events — the `featured-event` block (2026-09-05).
 *
 * ONE event, given the whole width, above the lists. The contract block is
 * written and tested on the backend side and cannot merge until this arm exists
 * (their block-coverage guard: "a new type ships with its renderer"), so the arm
 * ships first and the type is carried in `ONE_STOP_PENDING_BLOCK_TYPES` until the
 * contract admits it.
 *
 * NO SECOND REGISTRATION PATH. The band resolves the SAME `events` fragment the
 * list block resolves — the resolver returns a single item for this block — and
 * mounts the SAME `EventRow`, so the CTA, the visitor form, the waitlist branch,
 * the reference handling and the handoff are one implementation, not two that
 * drift. The band adds the frame around it: eyebrow, heading, and the emphasis.
 *
 * An empty fragment renders the band's empty line rather than nothing: a featured
 * slot that silently disappears reads to an author as a broken save.
 */
export function FeaturedEventBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "events");
  const eyebrow = configText(block.config, "eyebrow", ctx.locale) ?? t("events.featured.eyebrow");
  const title = configText(block.config, "title", ctx.locale) ?? t("events.featured.title");
  const subtitle = configText(block.config, "subtitle", ctx.locale);
  const ctaLabel = configText(block.config, "ctaLabel", ctx.locale) ?? t("events.cta");
  const showCounts = configBool(block.config, "showCapacity", true);
  const showWaitlist = configBool(block.config, "showWaitlistState", true);
  const item = fragment && fragment.items.length > 0 ? fragment.items[0] : null;

  return (
    <section
      className="lv-featured"
      style={{
        border: "1px solid var(--jrni-color-surface-border)",
        borderRadius: "var(--jrni-radius-md, 12px)",
        background: "var(--jrni-color-surface-card)",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <span
        style={{
          fontSize: "var(--jrni-font-size-L1)",
          fontWeight: "var(--jrni-font-weight-semibold)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--jrni-color-brand-1, var(--jrni-color-text-soft))",
        }}
      >
        {eyebrow}
      </span>
      <span
        style={{
          fontSize: "var(--jrni-font-size-P1)",
          fontWeight: "var(--jrni-font-weight-bold)",
          color: "var(--jrni-color-chrome-ink)",
        }}
      >
        {title}
      </span>
      {subtitle ? <span className="lv-row-detail">{subtitle}</span> : null}
      {item ? (
        <div className="lv-rowlist lv-tight">
          <EventRow
            item={item}
            t={t}
            locale={ctx.locale}
            ctaLabel={ctaLabel}
            showCounts={showCounts}
            showWaitlist={showWaitlist}
          />
        </div>
      ) : (
        <Empty>{t("events.featured.empty")}</Empty>
      )}
    </section>
  );
}
